import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

/**
 * Landing title screen: Quaternius nature wardrobe on an Arenal silhouette,
 * with the keel-billed toucan guide as the focal living prop.
 *
 * Nature: /assets/quaternius/nature (CC0 MegaKit subset)
 * Toucan: in-world soft stylized stand-in until a Poly Pizza glTF is dropped at
 * /assets/poly-pizza/toucan/toucan.glb (see EXPERIENCE.md).
 */

const NATURE = '/assets/quaternius/nature'
const TOUCAN_CANDIDATES = [
  '/assets/poly-pizza/toucan/toucan.glb',
  '/assets/poly-pizza/toucan/toucan.gltf',
]

const TREE_MODELS = ['CommonTree_1', 'CommonTree_2', 'CommonTree_3', 'TwistedTree_1'] as const
const BUSH_MODELS = ['Bush_Common', 'Bush_Common_Flowers'] as const
const GROUND_MODELS = ['Grass_Common_Tall', 'Fern_1', 'Plant_1_Big'] as const
const ROCK_MODELS = ['Rock_Medium_1', 'Pebble_Round_1'] as const

export interface LandingHero {
  dispose: () => void
}

function hash(seed: number): number {
  let h = seed | 0
  h = Math.imul(h ^ (h >>> 16), 2246822507)
  h = Math.imul(h ^ (h >>> 13), 3266489909)
  return ((h ^ (h >>> 16)) >>> 0) / 0xffffffff
}

function recolorForArenal(root: THREE.Object3D): void {
  root.traverse((node) => {
    const mesh = node as THREE.Mesh
    if (!mesh.isMesh) return
    mesh.castShadow = true
    mesh.receiveShadow = true
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    for (const entry of materials) {
      if (!entry) continue
      entry.side = THREE.DoubleSide
      const mat = entry as THREE.MeshStandardMaterial
      if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace
      if (mat.transparent || mat.alphaMap || /leaf|leaves|grass|flower|plant|fern/i.test(mat.name ?? mesh.name)) {
        mat.transparent = true
        mat.alphaTest = 0.45
        mat.depthWrite = true
      }
      if (/leaf|leaves|grass|flower|plant|fern/i.test(mat.name ?? mesh.name)) {
        mat.color?.offsetHSL(-0.02, 0.14, -0.05)
      }
    }
  })
}

async function loadGltf(loader: GLTFLoader, url: string): Promise<THREE.Object3D | null> {
  try {
    const gltf = await loader.loadAsync(url)
    const root = gltf.scene
    recolorForArenal(root)
    return root
  } catch {
    return null
  }
}

function makeToucan(): THREE.Group {
  const guide = new THREE.Group()
  guide.name = 'landing-toucan'
  const black = new THREE.MeshStandardMaterial({ color: 0x17251e, roughness: 0.75 })
  const yellow = new THREE.MeshStandardMaterial({
    color: 0xf3c94f,
    emissive: 0x805514,
    emissiveIntensity: 0.18,
    roughness: 0.65,
  })
  const orange = new THREE.MeshStandardMaterial({ color: 0xf18435, roughness: 0.6 })
  const white = new THREE.MeshBasicMaterial({ color: 0xfff8dc })

  const body = new THREE.Mesh(new THREE.SphereGeometry(0.72, 14, 10), black)
  body.scale.set(0.8, 1.15, 0.75)
  const chest = new THREE.Mesh(new THREE.SphereGeometry(0.46, 12, 8), yellow)
  chest.position.set(0.2, 0.12, 0.48)
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.44, 12, 9), black)
  head.position.set(0.08, 0.82, 0)
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.32, 1.4, 8), orange)
  beak.rotation.z = -Math.PI / 2
  beak.position.set(0.76, 0.86, 0)
  const eye = new THREE.Mesh(new THREE.SphereGeometry(0.075, 8, 6), white)
  eye.position.set(0.3, 0.96, 0.36)
  const wing = new THREE.Mesh(new THREE.SphereGeometry(0.43, 10, 7), black)
  wing.scale.set(0.5, 1.3, 0.34)
  wing.position.set(-0.34, 0.05, 0)
  guide.add(body, chest, head, beak, eye, wing)
  guide.userData.wing = wing
  return guide
}

function makeCone(): THREE.Group {
  const group = new THREE.Group()
  group.position.set(1, 0, -30)
  group.scale.setScalar(0.95)

  const cone = new THREE.Mesh(
    new THREE.ConeGeometry(15, 24, 36, 1, false),
    new THREE.MeshStandardMaterial({
      color: 0x3a5a44,
      roughness: 0.92,
      flatShading: true,
    }),
  )
  cone.castShadow = true
  cone.receiveShadow = true
  cone.position.y = 12
  const scar = new THREE.Mesh(
    new THREE.ConeGeometry(5.5, 14, 12, 1, false),
    new THREE.MeshStandardMaterial({
      color: 0x6a4a32,
      roughness: 1,
      transparent: true,
      opacity: 0.55,
      flatShading: true,
    }),
  )
  scar.position.set(-3.2, 9, 2)
  scar.rotation.z = 0.18
  const crater = new THREE.Mesh(
    new THREE.TorusGeometry(1.6, 0.35, 8, 20),
    new THREE.MeshStandardMaterial({ color: 0x4a5548, roughness: 0.95 }),
  )
  crater.rotation.x = Math.PI / 2
  crater.position.y = 23.5
  group.add(cone, scar, crater)
  return group
}

function makeLake(): THREE.Mesh {
  const lake = new THREE.Mesh(
    new THREE.CircleGeometry(22, 48),
    new THREE.MeshStandardMaterial({
      color: 0x3f8f9c,
      roughness: 0.18,
      metalness: 0.08,
      emissive: 0x123038,
      emissiveIntensity: 0.12,
    }),
  )
  lake.rotation.x = -Math.PI / 2
  lake.position.set(-1, 0.03, -6)
  lake.scale.set(1.7, 1, 0.9)
  lake.receiveShadow = true
  return lake
}

function makeGround(): THREE.Mesh {
  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(70, 56),
    new THREE.MeshStandardMaterial({ color: 0x2f5238, roughness: 1 }),
  )
  ground.rotation.x = -Math.PI / 2
  ground.position.y = 0
  ground.receiveShadow = true
  return ground
}

/** Place props in a foreground / shore band so the vista stays readable. */
function placeAlongBand(
  parent: THREE.Group,
  template: THREE.Object3D,
  count: number,
  seed: number,
  opts: {
    x: [number, number]
    z: [number, number]
    scale: [number, number]
    baseScale?: number
  },
): void {
  const base = opts.baseScale ?? 1
  for (let i = 0; i < count; i++) {
    const clone = template.clone(true)
    const s = opts.scale[0] + hash(seed + i * 47) * (opts.scale[1] - opts.scale[0])
    clone.scale.setScalar(base * s)
    clone.rotation.y = hash(seed + i * 59) * Math.PI * 2
    const x = opts.x[0] + hash(seed + i * 17) * (opts.x[1] - opts.x[0])
    const z = opts.z[0] + hash(seed + i * 31) * (opts.z[1] - opts.z[0])
    clone.position.set(x, 0, z)
    parent.add(clone)
  }
}

export function mountLandingHero(canvas: HTMLCanvasElement): LandingHero {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const weakGpu =
    window.matchMedia('(pointer: coarse)').matches && navigator.hardwareConcurrency <= 4

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: !weakGpu,
    alpha: false,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: true,
  })
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, weakGpu ? 1.25 : 1.75))
  renderer.setClearColor(0x8ec8dc, 1)
  renderer.shadowMap.enabled = !weakGpu
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = 1.35

  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0x8ec8dc)
  scene.fog = new THREE.Fog(0xc5ddd0, 55, 140)

  const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 240)
  camera.position.set(-4, 12, 40)
  camera.lookAt(0, 7.5, -20)

  const hemi = new THREE.HemisphereLight(0xe8f4ff, 0x4a6a48, 1.15)
  const fill = new THREE.AmbientLight(0xb8d4c8, 0.35)
  const sun = new THREE.DirectionalLight(0xffe8c0, 1.55)
  sun.position.set(22, 48, 28)
  sun.castShadow = !weakGpu
  if (!weakGpu) {
    sun.shadow.mapSize.set(1024, 1024)
    sun.shadow.camera.near = 2
    sun.shadow.camera.far = 110
    sun.shadow.camera.left = -40
    sun.shadow.camera.right = 40
    sun.shadow.camera.top = 40
    sun.shadow.camera.bottom = -40
  }
  scene.add(hemi, fill, sun)

  const root = new THREE.Group()
  scene.add(root)
  root.add(makeGround(), makeLake(), makeCone())

  // Soft cloud belt around the cone
  const cloudMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.5,
    roughness: 1,
    depthWrite: false,
  })
  for (let i = 0; i < 6; i++) {
    const puff = new THREE.Mesh(new THREE.SphereGeometry(2.8 + i * 0.15, 10, 8), cloudMat)
    puff.scale.set(2.1, 0.42, 1.2)
    puff.position.set(-16 + i * 5.8, 16 + (i % 2) * 1.4, -28 + (i % 3) * 1.6)
    root.add(puff)
  }

  const toucan = makeToucan()
  toucan.scale.setScalar(1.75)
  toucan.position.set(6.2, 5.6, 20)
  toucan.rotation.y = -1.05
  root.add(toucan)

  const loader = new GLTFLoader()
  const dressing = new THREE.Group()
  root.add(dressing)

  void (async () => {
    for (const name of TREE_MODELS) {
      const template = await loadGltf(loader, `${NATURE}/${name}.gltf`)
      if (!template) continue
      // Left and right shore frames — leave the lake / cone axis open.
      placeAlongBand(dressing, template, weakGpu ? 3 : 4, name.length * 13, {
        x: [-24, -7],
        z: [0, 18],
        scale: [0.95, 1.4],
        baseScale: 1.1,
      })
      placeAlongBand(dressing, template, weakGpu ? 2 : 4, name.length * 71, {
        x: [7, 24],
        z: [0, 17],
        scale: [0.9, 1.35],
        baseScale: 1.1,
      })
    }
    for (const name of BUSH_MODELS) {
      const template = await loadGltf(loader, `${NATURE}/${name}.gltf`)
      if (!template) continue
      placeAlongBand(dressing, template, weakGpu ? 4 : 6, name.length * 29, {
        x: [-18, 18],
        z: [6, 18],
        scale: [0.85, 1.4],
      })
    }
    for (const name of GROUND_MODELS) {
      const template = await loadGltf(loader, `${NATURE}/${name}.gltf`)
      if (!template) continue
      placeAlongBand(dressing, template, weakGpu ? 5 : 8, name.length * 41, {
        x: [-16, 16],
        z: [8, 20],
        scale: [1, 1.6],
      })
    }
    for (const name of ROCK_MODELS) {
      const template = await loadGltf(loader, `${NATURE}/${name}.gltf`)
      if (!template) continue
      placeAlongBand(dressing, template, 3, name.length * 53, {
        x: [-12, 12],
        z: [10, 18],
        scale: [0.9, 1.6],
      })
    }

    for (const url of TOUCAN_CANDIDATES) {
      try {
        const head = await fetch(url, { method: 'HEAD' })
        if (!head.ok) continue
      } catch {
        continue
      }
      const custom = await loadGltf(loader, url)
      if (!custom) continue
      toucan.clear()
      custom.scale.setScalar(1.2)
      toucan.add(custom)
      break
    }
  })()

  const resize = () => {
    const { clientWidth: w, clientHeight: h } = canvas
    if (w < 1 || h < 1) return
    renderer.setSize(w, h, false)
    camera.aspect = w / h
    camera.updateProjectionMatrix()
  }
  resize()
  const ro = new ResizeObserver(resize)
  ro.observe(canvas)

  let frame = 0
  let disposed = false
  const clock = new THREE.Clock()

  const tick = () => {
    if (disposed) return
    frame = requestAnimationFrame(tick)
    const t = clock.getElapsedTime()
    if (!reducedMotion) {
      camera.position.x = -4 + Math.sin(t * 0.1) * 1.1
      camera.position.y = 12 + Math.sin(t * 0.08) * 0.3
      camera.position.z = 40
      camera.lookAt(0, 7.5, -20)
      toucan.position.y = 5.6 + Math.sin(t * 1.6) * 0.32
      toucan.rotation.y = -1.05 + Math.sin(t * 0.5) * 0.12
      const wing = toucan.userData.wing as THREE.Object3D | undefined
      if (wing) wing.rotation.z = Math.sin(t * 8) * 0.35
    }
    renderer.render(scene, camera)
  }
  tick()

  return {
    dispose: () => {
      disposed = true
      cancelAnimationFrame(frame)
      ro.disconnect()
      renderer.dispose()
      scene.traverse((obj) => {
        const mesh = obj as THREE.Mesh
        if (mesh.geometry) mesh.geometry.dispose()
        const material = mesh.material
        if (!material) return
        const list = Array.isArray(material) ? material : [material]
        for (const entry of list) entry.dispose()
      })
    },
  }
}
